import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators, Dispatch } from 'redux';

import { CreditCardForm } from '@eika-felles/kyc-declaration-lib';
import { InfoMessage } from '@eika/attention-message';
import { StandardButton } from '@eika/button';
import { IconTypes } from '@eika/icon';
import { ErrorLabel } from '@eika/label';

import { kdkCompleted, kdkNextEvent, kycError, openStep } from '../../../actions';
import { DispatchWithState, StringKeyValue } from '../../../actions/types';
import { AppState } from '../../../reducers';
import { KdkReducer } from '../../../reducers/kdk';
import { LoaderReducer } from '../../../reducers/loader';
import { UserReducer } from '../../../reducers/user';
import { trackEvent, TrackingStep } from '../../../util/piwik-utils';
import Loading from '../../Messages/Loading';

import Lommebok from '../../../assets/img/lommebok.svg';

interface Props {
    action: {
        kdkNextEvent: () => DispatchWithState;
        openStep: (destinationStep: number) => void;
        kycError: () => void;
        kdkCompleted: () => void;
    };
    loader: LoaderReducer;
    text: StringKeyValue;
    user: UserReducer;
    kdk: KdkReducer;
}

const KycForm = (props: Props) => {
    const { loader, text, action, user, kdk } = props;
    const [kdkComplete, setKdkComplete] = useState(!kdk.showKYC);
    const { kdkNextEvent, openStep } = action;
    const { isSendingApplication } = loader;
    const { ssn } = user;
    const { kdkFormError, applicationBack } = text;

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    const scrollAndComplete = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        kdkNextEvent();
    };

    if (isSendingApplication) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return <Loading title={text.loadingKdk} />;
    }

    const getCorrectFormWithProps = () => {
        if (ssn) {
            return (
                // @ts-ignore
                <CreditCardForm
                    publicId={ssn}
                    statusCallback={(status) => {
                        setKdkComplete(true);
                        if (status === 'FAILED') action.kycError();
                        else action.kdkCompleted();
                    }}
                    goBackText={applicationBack}
                    goBackCallback={() => openStep(1)}
                />
            );
        }
        return <ErrorLabel text={kdkFormError} aria-roledescription="alert" className="input-error-message" />;
    };

    return (
        <div className="kyc-form">
            {!kdkComplete ? (
                <>
                    <InfoMessage
                        className="kyc-form__info-message"
                        icon={IconTypes.INFO_36}
                        text={text.kdkInfoMessage}
                    />
                    {getCorrectFormWithProps()}
                </>
            ) : (
                <div className="kyc-form__submit-application">
                    <img
                        src={Lommebok}
                        alt="Lommebok ikon"
                        title={text.kdkCreditCheckTitle}
                        style={{ maxWidth: '200px' }}
                        className="kyc-form__icon"
                    />

                    <h1 className="kyc-form__submit-application-title">{text.kdkCreditCheckTitle}</h1>
                    <p>{text.kdkCreditCheckDescription}</p>
                    <p className="kyc-form__submit-application-description">{text.kdkCreditCheckDescription2}</p>

                    <StandardButton
                        onClick={() => {
                            trackEvent(TrackingStep.Kyc);
                            scrollAndComplete();
                        }}
                    >
                        {text.kdkSubmitButton}
                    </StandardButton>
                </div>
            )}
        </div>
    );
};

const mapStateToProps = (state: AppState) => ({
    loader: state.loader,
    text: state.context.config.data.text,
    user: state.user,
    kdk: state.kdk,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
    action: bindActionCreators(
        {
            kdkNextEvent,
            openStep,
            kycError,
            kdkCompleted,
        },
        dispatch,
    ),
});

export default connect(mapStateToProps, mapDispatchToProps)(KycForm);
